import { useEffect, useRef, useState, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, Send, Megaphone, ArrowLeft, Circle, Loader2, Users, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

const BC_KEY_PREFIX = "broadcast:";

function timeLabel(iso) {
  if (!iso) return "";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es }); }
  catch { return ""; }
}

export default function TeamChat() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedThread, setSelectedThread] = useState(null); // { thread_type, peer_id? }
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  // ---- Auth: get session_token from cookie -------------------------------
  const sessionToken = useMemo(() => {
    const m = document.cookie.match(/session_token=([^;]+)/);
    return m ? m[1] : null;
  }, []);

  // ---- Data: threads (poll fallback every 15s) --------------------------
  const { data: threadsData, isLoading: threadsLoading, isError } = useQuery({
    queryKey: ["chat-threads"],
    queryFn: async () => (await api.get("/chat/threads")).data,
    retry: false,
    refetchInterval: 15_000,
  });

  // ---- Super Admin DM (org admin only) — loaded separately -------------
  const { data: saInboxData } = useQuery({
    queryKey: ["sa-dm-inbox"],
    queryFn: async () => (await api.get("/org/admin-inbox")).data,
    enabled: threadsData?.me?.org_role === "admin",
    retry: false,
    refetchInterval: 15_000,
  });
  const { data: saUnreadData } = useQuery({
    queryKey: ["sa-dm-unread"],
    queryFn: async () => (await api.get("/org/admin-inbox/unread")).data,
    enabled: threadsData?.me?.org_role === "admin",
    retry: false,
    refetchInterval: 20_000,
  });

  // Auto-select the broadcast thread first time.
  useEffect(() => {
    if (!selectedThread && threadsData?.broadcast) {
      setSelectedThread({ thread_type: "broadcast" });
    }
  }, [threadsData, selectedThread]);

  const isAdmin = threadsData?.me?.org_role === "admin";

  // ---- Data: messages of current thread ---------------------------------
  const messagesKey = ["chat-messages", selectedThread?.thread_type, selectedThread?.peer_id];
  const { data: messagesData, isFetching: msgsFetching } = useQuery({
    queryKey: messagesKey,
    queryFn: async () => {
      const q = selectedThread.thread_type === "direct"
        ? `?thread_type=direct&peer_id=${selectedThread.peer_id}`
        : `?thread_type=broadcast`;
      return (await api.get(`/chat/messages${q}`)).data;
    },
    enabled: !!selectedThread,
  });

  // Scroll to bottom when new messages arrive.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messagesData?.messages?.length]);

  // ---- Send -------------------------------------------------------------
  const [draft, setDraft] = useState("");

  const sendMutation = useMutation({
    mutationFn: async () => {
      const body = { thread_type: selectedThread.thread_type, text: draft.trim() };
      if (selectedThread.thread_type === "direct") body.recipient_id = selectedThread.peer_id;
      return (await api.post("/chat/messages", body)).data;
    },
    onSuccess: (msg) => {
      setDraft("");
      // Optimistically append the message to the current thread.
      qc.setQueryData(messagesKey, (prev) => prev
        ? { ...prev, messages: [...prev.messages, msg] }
        : { messages: [msg], thread_key: msg.thread_key },
      );
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo enviar el mensaje"),
  });

  const saSendMutation = useMutation({
    mutationFn: async () => (await api.post("/org/admin-inbox", { text: draft.trim() })).data,
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["sa-dm-inbox"] });
      qc.invalidateQueries({ queryKey: ["sa-dm-unread"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al responder"),
  });

  // ---- WebSocket: push notifications -----------------------------------
  useEffect(() => {
    if (!sessionToken || !threadsData) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsProto = backend.startsWith("https") ? "wss" : "ws";
    const host = backend.replace(/^https?:\/\//, "");
    const url = `${wsProto}://${host}/api/ws/chat?token=${sessionToken}`;

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.warn("Chat WS failed to connect:", e);
      return;
    }
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const obj = JSON.parse(ev.data);
        if (obj.type !== "chat_message") return;
        const msg = obj.message;
        // Append to current thread if it matches.
        qc.setQueryData(
          ["chat-messages",
            msg.thread_type,
            msg.thread_type === "direct"
              ? (msg.sender_id === threadsData.me.user_id ? msg.recipient_id : msg.sender_id)
              : undefined],
          (prev) => {
            if (!prev) return prev;
            if (prev.messages.some(m => m.message_id === msg.message_id)) return prev;
            return { ...prev, messages: [...prev.messages, msg] };
          }
        );
        // Bump threads list to refresh unread counts.
        qc.invalidateQueries({ queryKey: ["chat-threads"] });
      } catch { /* ignore malformed frames */ }
    };
    // Ping every 25s to keep the connection alive through k8s idle timeouts.
    const pingId = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
    }, 25_000);

    ws.onclose = () => { clearInterval(pingId); };
    ws.onerror = (e) => { console.warn("Chat WS error", e); };

    return () => {
      clearInterval(pingId);
      try { ws.close(); } catch { /* ignore */ }
    };
  }, [sessionToken, threadsData?.me?.user_id, qc]);

  // ---- Layout -----------------------------------------------------------
  if (threadsLoading) {
    return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;
  }
  if (isError || !threadsData) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="chat-not-in-org">
        <Users className="h-12 w-12 mx-auto text-muted-foreground" />
        <h1 className="font-display text-2xl font-semibold mt-4">Chat solo disponible con plan Corporativo</h1>
        <p className="text-muted-foreground mt-2">Contrata Corporativo para chatear con tu equipo dentro de Sprintroll.</p>
        <Button className="mt-6" onClick={() => navigate("/pricing")}>Ver planes</Button>
      </div>
    );
  }

  const currentThreadTitle = selectedThread?.thread_type === "broadcast"
    ? "Todo el equipo"
    : selectedThread?.thread_type === "sa"
    ? "Equipo Sprintroll"
    : (threadsData.direct.find(t => t.peer.user_id === selectedThread?.peer_id)?.peer.name
        || threadsData.direct.find(t => t.peer.user_id === selectedThread?.peer_id)?.peer.email
        || "");

  // Messages displayed vary by thread type
  const currentMessages = selectedThread?.thread_type === "sa"
    ? (saInboxData?.messages || [])
    : (messagesData?.messages || []);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="team-chat-page">
      <button
        onClick={() => navigate("/org")}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        data-testid="chat-back"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al panel
      </button>

      <div className="glass-panel rounded-3xl overflow-hidden grid md:grid-cols-[280px_1fr] h-[calc(100vh-160px)] min-h-[500px]">
        {/* Threads sidebar */}
        <aside className="border-r border-white/10 flex flex-col" data-testid="chat-threads-list">
          <div className="p-4 border-b border-white/10">
            <h1 className="font-display text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Chat del equipo
            </h1>
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Broadcast row */}
            <ThreadRow
              testId="chat-thread-broadcast"
              icon={<Megaphone className="h-4 w-4 text-amber-500" />}
              title={threadsData.broadcast.org_name || "Broadcast"}
              subtitle={isAdmin ? "Mensaje a todo el equipo" : "Solo el admin publica aquí"}
              last={threadsData.broadcast.last_message}
              unread={threadsData.broadcast.unread}
              active={selectedThread?.thread_type === "broadcast"}
              onClick={() => setSelectedThread({ thread_type: "broadcast" })}
            />
            <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Mensajes directos
            </div>
            {threadsData.direct.map(t => (
              <ThreadRow
                key={t.peer.user_id}
                testId={`chat-thread-${t.peer.user_id}`}
                avatar={t.peer.picture}
                title={t.peer.name || t.peer.email}
                subtitle={t.peer.email}
                last={t.last_message}
                unread={t.unread}
                active={selectedThread?.thread_type === "direct" && selectedThread?.peer_id === t.peer.user_id}
                onClick={() => setSelectedThread({ thread_type: "direct", peer_id: t.peer.user_id })}
              />
            ))}
            {threadsData.direct.length === 0 && (
              <p className="px-4 py-4 text-xs text-muted-foreground text-center">
                Aún no tienes compañeros en tu organización.
              </p>
            )}

            {/* Super Admin DM channel — only visible to org admins */}
            {threadsData.me?.org_role === "admin" && (saInboxData?.messages?.length > 0 || (saUnreadData?.unread ?? 0) > 0) && (
              <>
                <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  Soporte Sprintroll
                </div>
                <ThreadRow
                  testId="chat-thread-sa"
                  icon={<ShieldCheck className="h-4 w-4 text-red-500" />}
                  title="Equipo Sprintroll"
                  subtitle="Canal directo con Soporte / Admin"
                  last={saInboxData?.messages?.[saInboxData.messages.length - 1]}
                  unread={saUnreadData?.unread || 0}
                  active={selectedThread?.thread_type === "sa"}
                  onClick={() => setSelectedThread({ thread_type: "sa" })}
                />
              </>
            )}
          </div>
        </aside>

        {/* Message pane */}
        <section className="flex flex-col min-w-0" data-testid="chat-message-pane">
          {!selectedThread ? (
            <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
              Selecciona una conversación
            </div>
          ) : (
            <>
              <header className="border-b border-white/10 px-5 py-3 flex items-center gap-3">
                {selectedThread.thread_type === "broadcast"
                  ? <Megaphone className="h-5 w-5 text-amber-500" />
                  : selectedThread.thread_type === "sa"
                  ? <ShieldCheck className="h-5 w-5 text-red-500" />
                  : <MessageSquare className="h-5 w-5 text-blue-500" />}
                <div>
                  <h2 className="font-semibold" data-testid="chat-thread-title">{currentThreadTitle}</h2>
                  <p className="text-[11px] text-muted-foreground">
                    {selectedThread.thread_type === "broadcast"
                      ? "Todos los miembros de la organización lo verán"
                      : selectedThread.thread_type === "sa"
                      ? "Canal privado con el equipo Sprintroll"
                      : "Conversación privada"}
                  </p>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
                {msgsFetching && !messagesData && selectedThread.thread_type !== "sa" ? (
                  <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
                ) : currentMessages.length === 0 ? (
                  <p className="text-sm text-center text-muted-foreground py-8">
                    Aún no hay mensajes. {selectedThread.thread_type === "broadcast" && !isAdmin && " Espera a que el admin escriba."}
                    {selectedThread.thread_type === "sa" && " El equipo Sprintroll iniciará la conversación cuando lo requiera."}
                  </p>
                ) : (
                  currentMessages.map(msg => {
                    const isMe = selectedThread.thread_type === "sa"
                      ? msg.sender_role === "org_admin"
                      : msg.sender_id === threadsData.me.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={cn("flex gap-2 items-end", isMe && "flex-row-reverse")}
                        data-testid={`chat-msg-${msg.message_id}`}
                      >
                        {msg.sender_picture ? (
                          <img
                            src={msg.sender_picture}
                            alt=""
                            className="h-8 w-8 rounded-full flex-none object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className={cn(
                            "h-8 w-8 rounded-full grid place-items-center text-xs font-semibold flex-none",
                            selectedThread.thread_type === "sa" && !isMe
                              ? "bg-red-500/15 text-red-600 dark:text-red-300"
                              : "bg-blue-500/15 text-blue-600 dark:text-blue-300",
                          )}>
                            {(msg.sender_name || "?").slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className={cn("max-w-[70%]", isMe ? "text-right" : "text-left")}>
                          <div className="text-[11px] text-muted-foreground mb-0.5">
                            {isMe ? "Tú" : msg.sender_name}
                            <span className="ml-1.5" title={format(new Date(msg.created_at), "PPpp", { locale: es })}>
                              · {timeLabel(msg.created_at)}
                            </span>
                          </div>
                          <div
                            className={cn(
                              "rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words",
                              isMe
                                ? "bg-blue-600 text-white rounded-br-sm"
                                : selectedThread.thread_type === "sa"
                                ? "bg-red-500/10 border border-red-500/20 rounded-bl-sm"
                                : "bg-muted/70 rounded-bl-sm",
                            )}
                          >
                            {msg.text}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Composer */}
              {(selectedThread.thread_type === "direct" || (selectedThread.thread_type === "broadcast" && isAdmin) || selectedThread.thread_type === "sa") ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!draft.trim()) return;
                    if (selectedThread.thread_type === "sa") saSendMutation.mutate();
                    else sendMutation.mutate();
                  }}
                  className="border-t border-white/10 p-4 flex gap-2"
                  data-testid="chat-composer"
                >
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!draft.trim()) return;
                        if (selectedThread.thread_type === "sa") saSendMutation.mutate();
                        else sendMutation.mutate();
                      }
                    }}
                    placeholder={selectedThread.thread_type === "broadcast"
                      ? "Escribe un mensaje para todo el equipo… (Enter para enviar, Shift+Enter para nueva línea)"
                      : selectedThread.thread_type === "sa"
                      ? "Responde al equipo Sprintroll…"
                      : "Escribe un mensaje… (Enter para enviar)"}
                    className="min-h-[46px] max-h-[120px] resize-none"
                    rows={1}
                    data-testid="chat-input"
                  />
                  <Button
                    type="submit"
                    disabled={!draft.trim() || sendMutation.isPending || saSendMutation.isPending}
                    data-testid="chat-send"
                    className="self-end"
                  >
                    {(sendMutation.isPending || saSendMutation.isPending)
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Send className="h-4 w-4" />}
                  </Button>
                </form>
              ) : (
                <div className="border-t border-white/10 p-4 text-center text-xs text-muted-foreground">
                  Solo el administrador de la organización puede enviar mensajes al broadcast.
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ThreadRow({ testId, icon, avatar, title, subtitle, last, unread, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "w-full flex items-start gap-3 px-4 py-3 text-left transition border-b border-white/5",
        active
          ? "bg-blue-500/10 border-l-2 border-l-blue-500"
          : "hover:bg-white/40 dark:hover:bg-white/5",
      )}
    >
      {avatar ? (
        <img src={avatar} alt="" className="h-9 w-9 rounded-full object-cover flex-none" referrerPolicy="no-referrer" />
      ) : (
        <div className="h-9 w-9 rounded-full bg-muted grid place-items-center flex-none">
          {icon || <MessageSquare className="h-4 w-4" />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold truncate flex-1">{title}</p>
          {unread > 0 && (
            <span className="inline-flex items-center gap-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
              <Circle className="h-1.5 w-1.5 fill-current" /> {unread}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {last ? `${last.sender_id ? "" : ""}${last.text}` : subtitle}
        </p>
      </div>
    </button>
  );
}
